import React, { useState, useEffect } from 'react';
import { Settings, User, ChevronLeft, ChevronRight, Import } from 'lucide-react';
import './KYCPage.css';
import {formatCurrency, formatInterest} from "../../util/currency_lookup";
import {Card, CardContent, StatCard} from "../../components/card-components";
import DashboardBase  from "../DashboardBase";
import {Loader} from "../../components/loader";
import { useNavigate } from "react-router";
import UserSearch from '../../components/UserSearch';

const KYCPage = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const itemsPerPage = 8;
  const [currentPage, setCurrentPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  const navigate = useNavigate();

  useEffect(() => {
    // Fetch the transaction data from the backend
    fetchTransactions(currentPage, selectedUser);
  }, [currentPage]);

  const goToItem = (id) => {
    navigate(`/verification-details/${id}`);
  };

  const handleUserSelect = (user) => {
    // Call your fetchTransactions with the selected fund
    setSelectedUser(user);
    fetchTransactions(currentPage, user);
  };

  const fetchTransactions = (page, user) => {
    const token = localStorage.getItem('token');
    const indexOfLastTransaction = ((page - 1) * itemsPerPage);


    let url = `${process.env.REACT_APP_API_URL}/api/v1/identify?offset=${indexOfLastTransaction}&limit=${itemsPerPage}`
    if (user) {
      url += `&user_id=${encodeURIComponent(user.id)}`;
    }

    fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then((data) => {
        setDocuments(data.rows);
        setTotal(data.total);
        setLoading(false);
      })
      .catch((error) => {
        setError(error);
        setLoading(false);
      });
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getStatusClass = (status) => {
    switch (status.toLowerCase()) {
      case 'pending': return 'status-completed';
      case 'completed': return 'status-pending';
      case 'failed': return 'status-rejected';
      default: return 'status-default';
    }
  };

  if (loading) {
    <Loader loading={loading} />;
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  return (
    <DashboardBase
      page="kyc"
      content= {
            <div className="dashboard-home">
                {/* Secondary Navigation */}

                <div className="dashboard-header">
                        <h1>Summary</h1>
                </div>

                {/* Stats Grid */}
                <div className="stats-grid">
                <div className="stats-container">
                    <StatCard 
                    title="Deposits" 
                    value={formatCurrency(34380.19)} 
                    icon={() => <div className="text-xs">↓</div>}
                    />
                    <StatCard 
                    title="Disbursements" 
                    value={formatCurrency(12426.02)} 
                    icon={() => <div className="text-xs">↑</div>}
                    />
                    <StatCard 
                    title="Average deposit" 
                    value={formatCurrency(1380.12)} 
                    icon={() => <div className="text-xs">↓</div>}
                    />
                    <StatCard 
                    title="Average disbursement" 
                    value={formatCurrency(2270.78)} 
                    icon={() => <div className="text-xs">↑</div>}
                    />
                </div>
                </div>

                {/* Transactions Table */}
                <div className="transactions-section">
                <div className="stats-header">
                    <h2 className="stats-title">Documents</h2>
                    <select className="select-control">
                    <option>All</option>
                    </select>
                </div>
                <Card>
                    <div className="tabs-header">
                    <div className="tabs">
                    <div className="filters-container">
                            <div className="search-section">
                              <UserSearch onUserSelect={handleUserSelect} />
                            </div>
                        </div>
                    </div>
                    </div>
                    <div className="table-container">
                    <table>
                        <thead>
                        <tr>
                            <th>Status</th>
                            <th>Reject Reason</th>
                            <th>Identify Check</th>
                            <th>Address Check</th>
                            <th>Customer Names</th>
                            <th>Email</th>
                            <th>Verification</th>
                            <th></th>
                        </tr>
                        </thead>
                        <tbody>
                        {documents.map((user, idx) => (
                            <tr key={idx} className="table-row" onClick={() => goToItem(user.verification_id)}>
                            <td>
                                <span className={`status-badge ${getStatusClass(user.kyc_status)}`}>
                                  {user.kyc_status}
                                </span>
                            </td>
                            <td>
                              {user.rejected_reason}
                            </td>
                            <td>
                              <span className={`status-badge ${getStatusClass(user.document_status)}`}>
                                {user.document_status}
                              </span>
                            </td>
                            <td>
                              <span className={`status-badge ${getStatusClass(user.address_status)}`}>
                                {user.address_status}
                              </span>
                            </td>
                            <td>
                              <div className="stacked-text">
                                <span>{user.first_name}</span>
                                <span>{user.last_name}</span>
                              </div>
                            </td>
                            <td>
                              {user.email}
                            </td>
                            <td>
                              {user.verification_date}
                            </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                    {/* Pagination */}
                    <div className="pagination">
                    <div>
                        <p className="pagination-text">
                        Showing page {currentPage} of {Math.ceil(total / itemsPerPage)}
                        </p>
                    </div>
                    <div className="pagination-buttons">
                        <button
                        onClick={() => paginate(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="pagination-button"
                        >
                        <ChevronLeft className="icon" />
                        </button>
                        <button
                        onClick={() => paginate(p => p + 1)}
                        disabled={currentPage * itemsPerPage >= total}
                        className="pagination-button"
                        >
                        <ChevronRight className="icon" />
                        </button>
                    </div>
                    </div>
                </Card>
            </div>
        </div>
      }
    />
  );
};

export default KYCPage;
